import * as Yup from "yup";
import { ImageWithArtistId, Contributor } from "../../types/form";
import { CompanyTypeCategory } from "../../../utils/companyTypeClassification";
import { getSubgenreOptions } from "../../utils/selectUtils";
import { NONE_OF_THESE } from "../../utils/optionRules";

export interface DspMarketingNote {
  id: string;
  dspName: string;
  note: string;
}

export interface PlatformSpecificUpc {
  id: string;
  dspName: string;
  upc: string;
  notes?: string;
}

export interface ProvisionalFieldHistory {
  field: string;
  oldValue: string | string[];
  newValue: string | string[];
  updatedAt: string;
}

export interface PitchFormValues {
  // Basic Artist and Release Information
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string[];

  // Identifiers
  releaseUpc: string;
  focusTrackName?: string;
  trackIsrc: string;

  // Artist Details
  featuredArtists?: string;
  featuredArtistUri?: string;
  artistHometown?: string;
  artistCurrentTown?: string;

  // Release Descriptions
  altTitles?: string;
  shortDescription: string;
  expandedDescription?: string;
  longDescription: string;
  lyrics: string;
  songDescription?: string[];

  // Musical Characteristics
  mainGenre: string[];
  songMood: string[];
  language: string[];

  // Links and Media
  spotifyUri?: string;
  linkToListen?: string;
  promoPhotos?: string;
  promoPhotosCredit?: string;
  socialsLinks?: string;

  // Platform-Specific Details
  similarArtists?: string;
  asin?: string;
  TIDALpage?: string;
  soundCloudProfileUrl?: string;
  soundCloudContact?: string;
  distributor?: string;

  // Platform-Specific Flags and Priorities
  pinToThisIsPlaylist?: boolean;
  pitchToAudiomackTrending?: boolean;
  isPremierProgram?: boolean;

  // Subgenres for each platform
  spotifySubgenre?: string[];
  amazonSubgenre?: string[];
  deezerSubgenre?: string[];
  audiomackSubgenre?: string[];
  TIDALSubgenre?: string[];
  soundCloudSubgenre?: string[];

  // Spotify specific
  musicCulture?: string[];
  instruments?: string[];
  songStyle?: string[];
  spotifyPlaylist?: string[];

  // Amazon specific
  listenersLocation?: string[];
  songCategory?: string[];

  // Deezer specific
  priority?: string;
  deezerEditorialTeam?: string;
  deezerPlaylist?: string[];

  // TIDAL specific
  artistCountry?: string;
  LabelDistributor?: string;
  tidalArtistLevel?: string;
  tidalPriorityLevel?: string;
  tidalDolbyAtmos?: string;
  tidalLocalMarket?: string[];
  tidalBpm?: number;
  tidalConfirmation?: string | null;

  // SoundCloud specific
  promotionTerritories?: string[];
  freshMusicFriday?: string[];
  playlistConsideration?: string;
  soundCloudReleaseStage?: string;
  soundCloudLabel?: string;

  // Apple specific
  appleId?: string;
  appleTrackId?: string;
  appleAlbumId?: string;
  appleSubgenre?: string[];
  appleReleaseStage?: string;
  applePitchFrom?: string;
  applePitchTo?: string;
  appleHasTimedRelease?: string;
  appleTimedReleaseDate?: string;
  appleHasMotionArtwork?: string;
  appleHasSpatialAudio?: string;
  appleLabel?: string;
  applePromotionDate?: string;
  appleCountry?: string;

  // File uploads
  introImage?: File;

  // Status
  status?: string;

  // YouTube specific
  youtubePromotionSurface?: string[];
  youtubeReleaseStage?: string;
  // youtubeSongDetails?: string[];
  youtubePromotionDate?: string;
  youtubeTerritories?: string[];
  //asset files
  images?: ImageWithArtistId[];

  //siriusxm specific
  songWriters?: string;
  siriusXmChannel?: string;
  canadianContent?: boolean;

  // Contributors (writers, producers, engineers, performers, etc.)
  contributors?: Contributor[];

  // ADD DSP Marketing Notes
  dspMarketingNotes: DspMarketingNote[];
  // Platform-specific UPCs
  platformSpecificUpcs: PlatformSpecificUpc[];

  // Notable specific (Spotify)
  pitchToNotable?: boolean;
  notableSubmissionCategory?: string;
  notableGlobalMusicProgram?: string[];
  notableSubgenre?: string;
  notableSongwriter?: string;

  // Vevo specific fields
  vevoContentType?: string;
  vevoPremiereDate?: string;
  vevoContentProvider?: string;
  vevoSubgenre?: string[];
  vevoSubgenreOther?: string;
  vevoAdditionalNotes?: string;
  vevoPreviewLink?: string;
  vevoRep?: string;
  vevoRepOther?: string;
  vevoIsCtvOnly?: string;
  vevoIsRefeature?: string;

  // Provisional fields support
  provisionalFields?: string[];
  provisionalNote?: string;
  provisionalFieldHistory?: ProvisionalFieldHistory[];

  aimsRequestId?: string;
}

// Create a function that returns the validation schema based on selected DSPs
export const createPitchValidationSchema = (selectedDSPs: {
  spotify: boolean;
  amazon: boolean;
  deezer: boolean;
  TIDAL: boolean;
  audiomack: boolean;
  soundcloud: boolean;
  apple: boolean;
  youtube: boolean;
  vevo: boolean;
}, companyTypeCategory: CompanyTypeCategory = "other") => {
  return Yup.object().shape({
    // Basic required fields (always required)
    artistName: Yup.string().required("Artist name is required"),
    releaseTitle: Yup.string().required("Release title is required"),
    releaseDate: Yup.date()
      // .min(new Date(), "Release date must be in the future")
      .required("Release date is required"),
    configuration: Yup.array()
      .of(Yup.string())
      .min(1, "Configuration is required")
      .max(2, "Please select no more than 2 configuration types")
      .required("Configuration is required")
      .test(
        "vevo-requires-video",
        'Vevo requires "Video" to be selected in Configuration',
        function (value) {
          // Only validate if Vevo is selected
          if (!selectedDSPs.vevo) return true;
          // Check if Video is in configuration
          return Array.isArray(value) && value.includes("Video");
        },
      ),

    // Identifiers (always required)
    releaseUpc: Yup.string()
      .matches(/^\d+$/, "UPC must contain digits only")
      .required("Release UPC is required"),
    trackIsrc: Yup.string()
      .matches(/^[A-Z]{2}-?[A-Z0-9]{3}-?\d{2}-?\d{5}$/, "Invalid ISRC format")
      .required("Track ISRC is required"),

    // Focus track required for EP/Album
    focusTrackName: Yup.string().when("configuration", {
      is: (val: string[]) =>
        Array.isArray(val) && (val.includes("EP") || val.includes("Album")),
      then: (schema) => schema.required("Focus track is required for EP/Album"),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Descriptions (always required)
    shortDescription: Yup.string()
      .max(500, "Short description must be less than 500 characters")
      .required("Short description is required"),
    expandedDescription: Yup.string(),
    longDescription: Yup.string().required("Long description is required"),
    lyrics: Yup.string(),

    // Genre (always required)
    mainGenre: Yup.array()
      .of(Yup.string())
      .min(1, "Main genre is required")
      .required("Main genre is required"),

    songMood: Yup.array()
      .of(Yup.string())
      .min(1, "Song mood is required")
      .required("Song mood is required")
      .test(
        "none-of-these-alone",
        `"${NONE_OF_THESE}" can't be combined with another mood`,
        (value) => !value || value.length < 2 || !value.includes(NONE_OF_THESE),
      ),

    language: Yup.array()
      .of(Yup.string())
      .min(1, "Language is required")
      .required("Language is required"),

    songDescription: Yup.array()
      .of(Yup.string())
      .min(1, "Song description is required")
      .required("Song description is required"),

    // Links and Media (always required)
    linkToListen: Yup.string().url().required("Link to listen is required"),
    promoPhotos: Yup.string()
      .url("Must be a valid URL")
      .test(
        "promoPhotos-or-images-required",
        "Either a valid promo photo URL or at least one image is required",
        function (value) {
          const { images } = this.parent;
          const hasPromoPhoto = !!value;
          const hasImages = Array.isArray(images) && images.length > 0;
          return hasPromoPhoto || hasImages;
        },
      ),

    // Platform-specific subgenres (conditional)
    spotifySubgenre: selectedDSPs.spotify
      ? Yup.array().of(Yup.string()).required("Spotify Subgenre is Required").test("min-2-max-3", "", function (value) {
          const count = value?.length ?? 0;
          if (count === 0) return this.createError({ message: "Spotify Subgenre is Required" });
          if (count < 2) return this.createError({ message: "Select 1 more subgenre" });
          return true;
        })
      : Yup.array(),
    amazonSubgenre: selectedDSPs.amazon
      ? Yup.array().of(Yup.string()).required("Amazon Subgenre is Required").min(1, "Amazon Subgenre is Required").max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    deezerSubgenre: selectedDSPs.deezer
      ? Yup.array().of(Yup.string()).required("Deezer Subgenre is Required").min(1, "Deezer Subgenre is Required").max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    audiomackSubgenre: selectedDSPs.audiomack
      ? Yup.array().of(Yup.string()).required("Audiomack Subgenre is Required").min(1, "Audiomack Subgenre is Required").max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    // Some main genres have no TIDAL subgenre yet; there's nothing to pick then.
    TIDALSubgenre: selectedDSPs.TIDAL
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed").when("mainGenre", {
          is: (mainGenre: string[] | undefined) => getSubgenreOptions("TIDAL", mainGenre || []).length > 0,
          then: (schema) => schema.required("TIDAL Subgenre is Required").min(1, "TIDAL Subgenre is Required"),
        })
      : Yup.array(),
    soundCloudSubgenre: selectedDSPs.soundcloud
      ? Yup.array().of(Yup.string()).required("SoundCloud Subgenre is Required").min(1, "SoundCloud Subgenre is Required").max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),

    // Spotify specific (conditional)
    spotifyUri: selectedDSPs.spotify
      ? Yup.string().required("Spotify URI is required")
      : Yup.string(),
    artistHometown: selectedDSPs.spotify
      ? Yup.string().required("Artist Hometown is Required")
      : Yup.string(),
    artistCurrentTown: selectedDSPs.spotify
      ? Yup.string().required("Artist Current Town is Required")
      : Yup.string(),
    musicCulture: selectedDSPs.spotify
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Music Culture is Required")
          .max(2, "Maximum 2 music cultures allowed")
          .required("Music Culture is Required")
      : Yup.array(),
    instruments: selectedDSPs.spotify
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Instruments is Required")
          .required("Instruments is Required")
      : Yup.array(),
    songStyle: selectedDSPs.spotify
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Song Style is Required")
          .max(3, "Maximum 3 song styles allowed")
          .required("Song Style is Required")
      : Yup.array(),
    spotifyPlaylist: selectedDSPs.spotify
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 playlists allowed")
      : Yup.array(),

    // Amazon specific (conditional)
    listenersLocation: selectedDSPs.amazon
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Listeners Location is Required")
          .max(1, "Maximum 1 location allowed")
          .required("Listeners Location is Required")
      : Yup.array(),
    songCategory: selectedDSPs.amazon
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Song Category is Required")
          .max(3, "Maximum 3 categories allowed")
          .required("Song Category is Required")
      : Yup.array(),
    similarArtists: selectedDSPs.amazon
      ? Yup.string().required("Similar Artists is Required")
      : Yup.string(),

    // Deezer specific (conditional)
    priority: selectedDSPs.deezer
      ? Yup.string().required("Priority is Required")
      : Yup.string(),
    deezerEditorialTeam: selectedDSPs.deezer
      ? Yup.string().required("Deezer Editorial Team is Required")
      : Yup.string(),
    deezerPlaylist: selectedDSPs.deezer
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 playlists allowed")
      : Yup.array(),

    // TIDAL specific (conditional)
    TIDALpage: selectedDSPs.TIDAL
      ? Yup.string()
          .url("Must be a valid URL")
          .required("Tidal Page is Required")
      : Yup.string(),
    // TIDAL doesn't ask for the artist's country; it asks for Local Market.
    artistCountry: Yup.string(),
    LabelDistributor: selectedDSPs.TIDAL
      ? Yup.string().required("Label Distributor is Required")
      : Yup.string(),
    socialsLinks: selectedDSPs.TIDAL
      ? Yup.string().required("Socials Links is Required")
      : Yup.string(),
    tidalArtistLevel: selectedDSPs.TIDAL
      ? Yup.string().required("Artist Level is Required")
      : Yup.string(),
    tidalPriorityLevel: selectedDSPs.TIDAL
      ? Yup.string().required("TIDAL Priority Level is Required")
      : Yup.string(),
    tidalDolbyAtmos: selectedDSPs.TIDAL
      ? Yup.string().required("Delivered in Dolby Atmos is Required")
      : Yup.string(),
    tidalLocalMarket: Yup.array().of(Yup.string()),
    tidalBpm: Yup.number(),
    tidalConfirmation: Yup.string().nullable(),

    // Photo credit validation - required for each image when TIDAL is selected
    images: selectedDSPs.TIDAL
      ? Yup.array().test(
          "photo-credits-required",
          "Photo Credit is required for each image when TIDAL is selected",
          function (value) {
            if (!Array.isArray(value) || value.length === 0) return true;
            return value.every(
              (img: any) =>
                img.photoCredit && img.photoCredit.trim().length > 0,
            );
          },
        )
      : Yup.array(),

    promoPhotosCredit: Yup.string().test(
      "promoPhotosCredit-required",
      "Photo Credit is required when TIDAL is selected and a promo photo link is provided",
      function (value) {
        const { promoPhotos } = this.parent;
        if (selectedDSPs.TIDAL && !!promoPhotos) {
          return !!value && value.trim().length > 0;
        }
        return true;
      }
    ),

    // SoundCloud specific (conditional)
    soundCloudProfileUrl: selectedDSPs.soundcloud
      ? Yup.string()
          .url("Must be a valid URL")
          .required("SoundCloud Profile URL is Required")
      : Yup.string(),
    soundCloudContact: selectedDSPs.soundcloud
      ? Yup.string().required("SoundCloud Contact is Required")
      : Yup.string(),
    distributor: (selectedDSPs.soundcloud || selectedDSPs.deezer) && companyTypeCategory === "label"
      ? Yup.string().required("Distributor is Required")
      : Yup.string(),
    promotionTerritories: selectedDSPs.soundcloud
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Promotion Territories is Required")
          .required("Promotion Territories is Required")
      : Yup.array(),
    freshMusicFriday: selectedDSPs.soundcloud
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Fresh Music Friday is Required")
          .required("Fresh Music Friday is Required")
      : Yup.array(),
    playlistConsideration: selectedDSPs.soundcloud
      ? Yup.string().required("Playlist Consideration is Required")
      : Yup.string(),
    soundCloudReleaseStage: selectedDSPs.soundcloud
      ? Yup.string().required("Release Stage is Required")
      : Yup.string(),
    soundCloudLabel: (selectedDSPs.soundcloud || selectedDSPs.deezer) && companyTypeCategory === "distributor"
      ? Yup.string().required("Label is Required")
      : Yup.string(),

    // Apple specific (conditional)
    appleId: selectedDSPs.apple ? Yup.string().required("Apple Artist ID is required") : Yup.string(),
    appleTrackId: Yup.string(),
    appleAlbumId: selectedDSPs.apple ? Yup.string().required("Apple Album ID is required") : Yup.string(),
    appleSubgenre: selectedDSPs.apple
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Apple Subgenre is Required")
          .max(3, "Maximum 3 subgenres allowed")
          .required("Apple Subgenre is Required")
      : Yup.array(),
    appleReleaseStage: Yup.string(),
    applePitchFrom: selectedDSPs.apple
      ? Yup.string().required("Pitch From is required")
      : Yup.string(),
    applePitchTo: selectedDSPs.apple
      ? Yup.string().required("Pitch To is required")
      : Yup.string(),
    appleHasTimedRelease: Yup.string(),
    appleTimedReleaseDate: Yup.string(),
    appleHasMotionArtwork: Yup.string(),
    appleHasSpatialAudio: Yup.string(),
    appleLabel: Yup.string(),
    applePromotionDate: Yup.string(),
    appleCountry: Yup.string(),

    // Optional fields (not conditional)
    featuredArtists: Yup.string(),
    featuredArtistUri: Yup.string(),
    altTitles: Yup.string(),
    asin: Yup.string(),

    // Booleans
    pinToThisIsPlaylist: Yup.boolean(),
    pitchToNotable: Yup.boolean(),
    notableSubmissionCategory: selectedDSPs.spotify
      ? Yup.string().test(
          "notable-submission-category-required",
          "Submission Category is required",
          function (value) {
            const { pitchToNotable } = this.parent;
            if (pitchToNotable) {
              return !!value && value.length > 0;
            }
            return true;
          },
        )
      : Yup.string().notRequired(),
    notableGlobalMusicProgram: Yup.array().of(Yup.string()),
    notableSubgenre: selectedDSPs.spotify
      ? Yup.string().test(
          "notable-subgenre-required",
          "Notable Subgenre is required",
          function (value) {
            const { pitchToNotable } = this.parent;
            if (pitchToNotable) {
              return !!value && value.length > 0;
            }
            return true;
          },
        )
      : Yup.string().notRequired(),
    notableSongwriter: selectedDSPs.spotify
      ? Yup.string().test(
          'notable-songwriter-required',
          'Songwriter is required',
          function (value) {
            const { pitchToNotable } = this.parent;
            if (pitchToNotable) {
              return !!value && value.trim().length > 0;
            }
            return true;
          }
        )
      : Yup.string().notRequired(),
    pitchToAudiomackTrending: Yup.boolean(),
    isPremierProgram: Yup.boolean(),

    // Status
    status: Yup.string(),

    // YouTube specific (conditional)
    youtubePromotionSurface: selectedDSPs.youtube
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Promotion Surface is Required")
          .required("Promotion Surface is Required")
      : Yup.array(),
    youtubeReleaseStage: selectedDSPs.youtube
      ? Yup.string().required("Release Stage is Required")
      : Yup.string(),
    // youtubeSongDetails: selectedDSPs.youtube
    //   ? Yup.array()
    //       .of(Yup.string())
    //       .min(1, "Song Details is Required")
    //       .max(2, "Maximum 2 song details allowed")
    //       .required("Song Details is Required")
    //   : Yup.array(),
    youtubePromotionDate: selectedDSPs.youtube
      ? Yup.date()
          .min(new Date(), "Promotion date must be in the future")
          .required("Promotion date is required")
      : Yup.date(),
    youtubeTerritories: selectedDSPs.youtube
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Territories is Required")
          .max(3, "Maximum 3 territories allowed")
          .required("Territories is Required")
      : Yup.array(),

    // Vevo specific (conditional) - requires Video in configuration
    vevoContentType: selectedDSPs.vevo
      ? Yup.string().required("Content Type is required for Vevo")
      : Yup.string(),
    vevoPremiereDate: selectedDSPs.vevo
      ? Yup.date().required("Premiere Date is required for Vevo")
      : Yup.date(),
    vevoContentProvider: selectedDSPs.vevo
      ? Yup.string().required("Content Provider is required for Vevo")
      : Yup.string(),
    vevoSubgenre: selectedDSPs.vevo
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Subgenre is required for Vevo")
          .max(2, "Maximum 2 subgenres allowed")
          .required("Subgenre is required for Vevo")
      : Yup.array(),
    vevoSubgenreOther: Yup.string().when(["vevoSubgenre"], {
      is: (vevoSubgenre: string[]) =>
        Array.isArray(vevoSubgenre) && vevoSubgenre.includes("Other"),
      then: (schema) =>
        selectedDSPs.vevo
          ? schema.required("Please specify other subgenre")
          : schema,
      otherwise: (schema) => schema,
    }),
    vevoAdditionalNotes: selectedDSPs.vevo
      ? Yup.string().required("This field is required for Vevo")
      : Yup.string(),
    vevoPreviewLink: Yup.string().url("Must be a valid URL"),
    vevoRep: selectedDSPs.vevo
      ? Yup.string().required("Vevo Rep is required")
      : Yup.string(),
    vevoRepOther: Yup.string().when(["vevoRep"], {
      is: (vevoRep: string) => vevoRep === "Other",
      then: (schema) =>
        selectedDSPs.vevo
          ? schema.required("Please specify Vevo Rep name")
          : schema,
      otherwise: (schema) => schema,
    }),
    vevoIsCtvOnly: selectedDSPs.vevo
      ? Yup.string().required("Please specify if this is for CTV only")
      : Yup.string(),
    vevoIsRefeature: selectedDSPs.vevo
      ? Yup.string().required("Please specify if this is a re-feature")
      : Yup.string(),

    // ADD DSP Marketing Notes validation
    dspMarketingNotes: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          dspName: Yup.string().required(),
          note: Yup.string()
            .max(200, "Marketing note must be 200 characters or less")
            .required("Marketing note is required"),
        }),
      )
      .optional(),

    // Platform-specific UPCs validation
    platformSpecificUpcs: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          dspName: Yup.string().required("Platform is required"),
          upc: Yup.string()
            .matches(/^\d+$/, "UPC must contain digits only")
            .required("UPC is required"),
          notes: Yup.string()
            .max(300, "Notes must be 300 characters or less")
            .optional(),
        }),
      )
      .optional(),

    // Provisional fields
    provisionalFields: Yup.array().of(Yup.string()).optional(),
    provisionalNote: Yup.string().max(500, "Note must be 500 characters or less").optional(),
    provisionalFieldHistory: Yup.array().optional(),

    // Must be declared or Yup strips it from the validated values on submit
    aimsRequestId: Yup.string().optional().nullable(),
  });
};

// Keep the old schema for backward compatibility (but it will be deprecated)
export const pitchValidationSchema = Yup.object().shape({
  // Basic required fields
  artistName: Yup.string().required("Artist name is required"),
  releaseTitle: Yup.string().required("Release title is required"),
  releaseDate: Yup.date()
    .min(new Date(), "Release date must be in the future")
    .required("Release date is required"),
  configuration: Yup.array()
    .of(Yup.string())
    .min(1, "Configuration is required")
    .max(2, "Please select no more than 2 configuration types")
    .required("Configuration is required"),

  // Identifiers
  releaseUpc: Yup.string()
    .matches(/^\d+$/, "UPC must contain digits only")
    .required("Release UPC is required"),
  trackIsrc: Yup.string()
    .matches(/^[A-Z]{2}-?[A-Z0-9]{3}-?\d{2}-?\d{5}$/, "Invalid ISRC format")
    .required("Track ISRC is required"),

  // Focus track required for EP/Album
  focusTrackName: Yup.string().when("configuration", {
    is: (val: string[]) =>
      Array.isArray(val) && (val.includes("EP") || val.includes("Album")),
    then: (schema) => schema.required("Focus track is required for EP/Album"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Descriptions
  shortDescription: Yup.string()
    .max(500, "Short description must be less than 500 characters")
    .required("Short description is required"),
  expandedDescription: Yup.string(),
  longDescription: Yup.string().required("Long description is required"),
  lyrics: Yup.string(),

  // Genre
  mainGenre: Yup.array()
    .of(Yup.string())
    .min(1, "Main genre is required")
    .required("Main genre is required"),

  songMood: Yup.array()
    .of(Yup.string())
    .min(1, "Song mood is required")
    .required("Song mood is required"),

  language: Yup.array()
    .of(Yup.string())
    .min(1, "Language is required")
    .required("Language is required"),

  songDescription: Yup.array()
    .of(Yup.string())
    .min(1, "Song description is required")
    .required("Song description is required"),

  // URL validations
  linkToListen: Yup.string().url().required("Link to listen is required"),
  promoPhotos: Yup.string().url().required("Promo photos are required"),
  spotifyUri: Yup.string().required("Spotify URI is required"),
  TIDALpage: Yup.string()
    .url("Must be a valid URL")
    .required("Tidal Page is Required"),
  soundCloudProfileUrl: Yup.string()
    .url("Must be a valid URL")
    .required("SoundCloud Profile URL is Required"),

  isEmail: Yup.boolean().default(false),

  // Platform-specific subgenres
  spotifySubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  amazonSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  deezerSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  audiomackSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  TIDALSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  soundCloudSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),

  // Spotify specific
  musicCulture: Yup.array()
    .of(Yup.string())
    .min(1, "Music Culture is Required")
    .max(2, "Maximum 2 music cultures allowed")
    .required("Music Culture is Required"),
  instruments: Yup.array()
    .of(Yup.string())
    .min(1, "Instruments is Required")
    .required("Instruments is Required"),
  songStyle: Yup.array()
    .of(Yup.string())
    .min(1, "Song Style is Required")
    .max(3, "Maximum 3 song styles allowed")
    .required("Song Style is Required"),

  // Amazon specific
  listenersLocation: Yup.array()
    .of(Yup.string())
    .min(1, "Listeners Location is Required")
    .max(1, "Maximum 1 location allowed")
    .required("Listeners Location is Required"),
  songCategory: Yup.array()
    .of(Yup.string())
    .min(1, "Song Category is Required")
    .max(3, "Maximum 3 categories allowed")
    .required("Song Category is Required"),

  // SoundCloud specific
  promotionTerritories: Yup.array()
    .of(Yup.string())
    .min(1, "Promotion Territories is Required")
    .required("Promotion Territories is Required"),
  freshMusicFriday: Yup.array()
    .of(Yup.string())
    .min(1, "Fresh Music Friday is Required")
    .required("Fresh Music Friday is Required"),
  soundCloudContact: Yup.string().required("SoundCloud Contact is Required"),

  // Apple specific
  appleId: Yup.string(),
  appleTrackId: Yup.string(),
  appleAlbumId: Yup.string(),
  appleReleaseStage: Yup.string(),

  // Optional strings
  featuredArtists: Yup.string(),
  featuredArtistUri: Yup.string(),
  artistHometown: Yup.string().required("Artist Hometown is Required"),
  artistCurrentTown: Yup.string().required("Artist Current Town is Required"),
  altTitles: Yup.string(),
  socialsLinks: Yup.string().required("Socials Links is Required"),
  similarArtists: Yup.string().required("Similar Artists is Required"),
  asin: Yup.string(),
  distributor: Yup.string().required("Distributor is Required"),
  priority: Yup.string().required("Priority is Required"),
  deezerEditorialTeam: Yup.string().required(
    "Deezer Editorial Team is Required",
  ),
  artistCountry: Yup.string().required("Artist Country is Required"),
  LabelDistributor: Yup.string().required("Label Distributor is Required"),
  playlistConsideration: Yup.string().required(
    "Playlist Consideration is Required",
  ),

  // Booleans
  pinToThisIsPlaylist: Yup.boolean(),
  pitchToAudiomackTrending: Yup.boolean(),
  isPremierProgram: Yup.boolean(),

  // Status
  status: Yup.string(),
});
